import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Get language prefix for routes
  const getLangPrefix = () => {
    return i18n.language !== 'en' ? `/${i18n.language}` : '';
  };

  const navLinks = [
    { name: 'Home', path: `${getLangPrefix()}/`, key: 'home' },
    { name: t('nav.directReserve'), path: `${getLangPrefix()}/direct-reserve`, key: 'directReserve' },
    { name: t('nav.contact'), path: `${getLangPrefix()}/contact`, key: 'contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-kitco-navy text-white sticky top-0 z-50 shadow-xl border-b-2 border-kitco-gold/30" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 md:h-24">
          {/* Logo */}
          <Link to={`${getLangPrefix()}/`} className="flex items-center transition-transform hover:scale-105" data-testid="logo-link">
            <img
              src="/logos/kitco-yellow-light-grey.svg"
              alt="Kitco Digital Metals"
              className="h-10 md:h-12 w-auto"
              data-testid="logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => window.scrollTo(0, 0)}
                data-testid={`nav-link-${link.key}`}
                className={`text-lg font-semibold transition-all duration-200 hover:text-kitco-gold hover:scale-105 ${
                  isActive(link.path) ? 'text-kitco-gold border-b-2 border-kitco-gold' : 'text-white'
                } py-2`}
              >
                {link.name}
              </Link>
            ))}
            <LanguageSelector />
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md hover:bg-white/10 focus:outline-none transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-grey-800 border-t border-kitco-gold/20" data-testid="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                data-testid={`mobile-nav-link-${link.key}`}
                className={`block px-3 py-2 rounded-md text-base font-semibold transition-colors ${
                  isActive(link.path)
                    ? 'bg-kitco-gold text-kitco-navy'
                    : 'text-white hover:bg-kitco-gold/20 hover:text-kitco-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="px-3 py-2">
              <LanguageSelector />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;